import React from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Package, Megaphone, FileText, ClipboardList,
  ShoppingCart, TrendingUp, Bell, ArrowRight, Clock, CheckCircle
} from 'lucide-react';

const RECENT_ORDERS = [
  { id: 'NGX-2024-0891', item: 'Electrode Pads — 50pk', date: 'Feb 24, 2026', status: 'Delivered', amount: '$142.00' },
  { id: 'NGX-2024-0876', item: 'EMS Lead Wires (10 sets)', date: 'Feb 17, 2026', status: 'Shipped', amount: '$89.00' },
  { id: 'NGX-2024-0855', item: 'Custom Order — Protocol Pads', date: 'Feb 10, 2026', status: 'Processing', amount: '$315.00' },
];

const STATUS_BADGE = {
  Delivered: 'badge-active',
  Shipped: 'badge-teal',
  Processing: 'badge-pending',
};

export default function Dashboard({ setCurrentPage }) {
  const { user, profile } = useAuth();
  const displayName = profile?.full_name?.split(' ')[1] || profile?.full_name || 'Provider';

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const STATS = [
    { label: 'Total Orders', value: '47', sub: 'All time', icon: ShoppingCart, iconClass: 'blue', page: 'supplies' },
    { label: 'Active Subscription', value: 'Active', sub: 'Preferred Provider', icon: CheckCircle, iconClass: 'teal', page: 'account' },
    { label: 'Resources Available', value: '28', sub: 'Docs & marketing', icon: FileText, iconClass: 'pink', page: 'documents' },
    { label: 'YTD Spend', value: '$6,240', sub: 'Supplies & orders', icon: TrendingUp, iconClass: 'orange', page: 'orders' },
  ];

  const QUICK_ACTIONS = [
    {
      id: 'supplies',
      title: 'Reorder Supplies',
      desc: 'Electrode pads, lead wires & more',
      icon: Package,
      variant: '',
    },
    {
      id: 'marketing',
      title: 'Marketing Materials',
      desc: 'Brochures, social assets & signage',
      icon: Megaphone,
      variant: 'teal',
    },
    {
      id: 'documents',
      title: 'Documents & Forms',
      desc: 'Clinical forms, contracts & guides',
      icon: FileText,
      variant: 'pink',
    },
    {
      id: 'custom-order',
      title: 'Custom Order',
      desc: 'Request specialized configurations',
      icon: ClipboardList,
      variant: '',
    },
  ];

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{greeting}, Dr. {displayName}</div>
          <div className="page-subtitle">
            {profile?.practice_name || 'Your Practice'} · Preferred Provider Dashboard
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setCurrentPage('supplies')}>
          <ShoppingCart size={15} />
          Reorder Supplies
        </button>
      </div>

      <div className="page-body">
        {/* ANNOUNCEMENT */}
        <div className="announcement-banner mb-24">
          <div>
            <div className="announcement-title">🆕 New: NGX-PRO Electrode Series Available</div>
            <div className="announcement-text">Extended-wear electrode pads — 40% longer adhesion. Now available in the supply store.</div>
          </div>
          <button className="btn btn-outline" style={{ borderColor: 'rgba(255,255,255,0.6)', color: 'white', whiteSpace: 'nowrap', flexShrink: 0 }}
            onClick={() => setCurrentPage('supplies')}>
            Shop Now <ArrowRight size={13} />
          </button>
        </div>

        {/* STATS */}
        <div className="stats-grid">
          {STATS.map(s => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                className="stat-card"
                onClick={() => setCurrentPage(s.page)}
                style={{ cursor: 'pointer' }}
              >
                <div className="stat-info">
                  <div className="stat-label">{s.label}</div>
                  <div className="stat-value">{s.value}</div>
                  <div className="stat-sub">{s.sub}</div>
                </div>
                <div className={`stat-icon ${s.iconClass}`}>
                  <Icon size={20} />
                </div>
              </div>
            );
          })}
        </div>

        {/* QUICK ACTIONS */}
        <div className="section-title">Quick Actions</div>
        <div className="quick-actions-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {QUICK_ACTIONS.map(action => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                className={`quick-action-card ${action.variant}`}
                onClick={() => setCurrentPage(action.id)}
              >
                <div className="action-icon-wrap">
                  <Icon size={20} />
                </div>
                <div>
                  <div className="action-title">{action.title}</div>
                  <div className="action-desc">{action.desc}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* RECENT ORDERS */}
        <div className="section-title" style={{ marginTop: 28 }}>Recent Orders</div>
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Order #</th>
                <th>Item</th>
                <th>Date</th>
                <th>Status</th>
                <th>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {RECENT_ORDERS.map(order => (
                <tr key={order.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{order.id}</td>
                  <td style={{ fontWeight: 500, color: '#1e293b' }}>{order.item}</td>
                  <td style={{ color: '#64748b' }}>{order.date}</td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[order.status]}`}>{order.status}</span>
                  </td>
                  <td style={{ fontWeight: 600 }}>{order.amount}</td>
                  <td>
                    <button className="btn btn-secondary btn-sm">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-outline btn-sm">
            View All Orders <ArrowRight size={13} />
          </button>
        </div>
      </div>
    </>
  );
}
